import { useEffect, useState } from "react";
import axios from "axios";
import { handleBillingError, readBlobError, downloadBlob, filenameFrom } from "./useBilling";

const useDesigns = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null)
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [bgImage, setBgImage] = useState(null)
  const [draft, setDraft] = useState(false)

  const getLastPathPart = () => {
    // Remove any query parameters and hash from the pathname
    const cleanPath = window.location.pathname.replace(/[?#].*$/, '');
    // Split the path and filter out any empty parts
    const pathParts = cleanPath.split('/').filter(Boolean);
    // Get the last part or provide a fallback message
    return pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'No path found';
  };


  useEffect(() => {
    if (token && pendingUpdate) {
      triggerUpdate(pendingUpdate);
      setPendingUpdate(null);
    }
  }, [token, pendingUpdate]);

  const update = (updateData) => {
    if (token) {
      triggerUpdate(updateData);
    } else {
      setPendingUpdate(updateData);  // Save the update until token is available
    }
  };


  useEffect(() => {

    // Set the initial last part of the URL
    setToken(getLastPathPart());

    // Update the last part on back/forward navigation
    const handlePopState = () => {
      setToken(getLastPathPart());
    };

    // Listen to the popstate event
    window.addEventListener('popstate', handlePopState);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchCertificate();
  }, [token]);

  const fetchCertificate = () => {
    if (!token || token === null) {
      return
    }

    axios.get(`/iframe/certificates/${token}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })
      .then((response) => {
        setCertificate(response.data);
      });
  }

  // Both endpoints are metered, so they are fetched as blobs: opening a 402 JSON body as
  // a page would just show raw JSON inside the embed.
  const download = (format) => {
    if (!token) return;

    setLoading(true);

    axios.get(`/iframe/certificates/${token}/${format}`, {
      responseType: "blob",
      withCredentials: true,
    }).
      then((response) => {
        downloadBlob(response.data, filenameFrom(response, `certificate.${format}`));
      }).
      catch(async (error) => {
        if (handleBillingError(await readBlobError(error))) return;

        console.error(`Failed to download ${format}:`, error);
      }).
      finally(() => {
        setLoading(false);
      });
  }

  const openPdf = () => download("pdf");

  const openImage = () => download("png");

  //update the template 
  const triggerUpdate = ({ data }) => {

    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    setLoading(true);
    axios.patch(`/iframe/certificates/${token}`,
      { data },
      {
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    ).
      then((response) => {
        if (response.status === 200) {
          console.log("Login successful", response.data);
          setDraft(true)
          // Redirect to the dashboard
        }
      }).
      catch((error) => {
        console.log(error);
      }).
      finally(() => {
        setLoading(false);
      });
  }

  //fetch the templates
  const fetchTemplates = () => {
    if (!token || token === null) {
      return
    }
    axios.get(`/iframe/templates/${token}`, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })
      .then((response) => {
        setTemplates(response.data);

        console.log("RESPONSE", response.data)
      });

  };

  //Updated the background image 
  const uploadBg = (formData) => {
    formData.append("token", token)
    setLoading(true);
    axios.post(`/iframe/certificates/upload_bg`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data"
        }
      }
    ).
      then((response) => {
        if (response.status === 200) {
          console.log("Login successful");
          // Redirect to the dashboard
          setBgImage(response.data.url);
        }
      }).
      catch((error) => {
        console.log(error);
      }).
      finally(() => {
        setLoading(false);
      });
  }



  //Publish the design 

  const publish = ({ id }) => {
    setLoading(true)
    setDraft(false)
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    axios.post(`/iframe/certificates/${token}/publish`, {}, {
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }).
      then((response) => {
        if (response.status === 200) {
          setDraft(false)
        }
      }).catch((response) => {
        console.log("ERROR", response)
      }).finally(() => {
        setLoading(false)
      })
  }

  const uploadImage = ({ formData, addImage = null, addSvgImage = null }) => {
    setLoading(true);
    formData.append("token", token)
    axios.post(`/iframe/certificates/upload_image`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data"
        }
      }
    ).
      then((response) => {
        if (response.status === 200) {
          if (response.data.file_type === "svg") {
            addSvgImage({
              image: response.data.url,
              name: response.data.name
            });
          } else {
            addImage({
              url: response.data.url,
              name: response.data.name
            });
          }

        }
      }).
      catch((error) => {
        console.log(error);
      }).
      finally(() => {
        setLoading(false);
      });
  }

  return {
    templates,
    fetchTemplates,
    loading,
    update,
    publish,
    uploadBg,
    openPdf,
    openImage,
    bgImage,
    uploadImage,
    draft
  };
}

export default useDesigns;  